function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import React, { useEffect, useState, Fragment, forwardRef, useRef } from 'react';
import cn from 'clsx';
import ResizeObserver from 'resize-observer-polyfill';
import { string, node, bool, number, object } from 'prop-types';
import { ScrollSyncPane } from 'react-scroll-sync';

/**
 * ScrollableContainer component.
 */
const ScrollableContainer = /*#__PURE__*/forwardRef(({
  children,
  className,
  initialOffset,
  synced,
  width,
  style,
  ...restProps
}, ref) => {
  let mainContainerRef = useRef();
  if (ref) {
    mainContainerRef = ref;
  }
  const transformContainerRef = useRef();
  const transform = initialOffset ? `translateX(${initialOffset}px)` : null;
  const [transformContainerStyle, setTransformContainerStyle] = useState({
    transform
  });
  const [widthContainerStyle, setWidthContainerStyle] = useState({
    minWidth: width
  });

  /**
   * Enable scrolling on a container that was initially offset.
   *
   * Once the client-side JS runs replace the SSR-friendly transform offsets
   * with a scrollable container set to the correct position. This means the
   * container should render scrolled to the correct position before the
   * scrolling behaviour kicks in.
   */
  useEffect(() => {
    if (transform) {
      setTransformContainerStyle(null);
      mainContainerRef.current.style.overflowX = 'auto';
      mainContainerRef.current.scrollLeft = initialOffset * -1;
    }

    // The ResizeObserver is used in part so that any CSS changes applied
    // after the initial render are respected.
    const observer = new ResizeObserver(() => {
      if (!width) {
        setWidthContainerStyle({
          minWidth: transformContainerRef.current.firstChild.scrollWidth
        });
      }
    });
    observer.observe(transformContainerRef.current.firstChild);
    return () => {
      observer.disconnect();
    };
  }, []);
  const Wrapper = synced ? ScrollSyncPane : Fragment;
  return /*#__PURE__*/React.createElement(Wrapper, null, /*#__PURE__*/React.createElement("div", _extends({
    className: cn('scrollable-container', className, initialOffset && 'scrollable-container--with-initial-offset'),
    ref: mainContainerRef,
    style: style
  }, restProps), /*#__PURE__*/React.createElement("div", {
    className: "scrollable-container__width-container",
    style: widthContainerStyle
  }, /*#__PURE__*/React.createElement("div", {
    style: transformContainerStyle,
    ref: transformContainerRef
  }, children))));
});
ScrollableContainer.defaultProps = {
  className: null,
  children: null,
  initialOffset: 0,
  synced: false,
  style: null,
  width: null
};
ScrollableContainer.propTypes = process.env.NODE_ENV !== "production" ? {
  /** Additional class names to add to the component root. */
  className: string,
  /** The contents of the container. */
  children: node,
  /** Any initial offset to render the container at when initially rendered. */
  initialOffset: number,
  /** Custom inline style overrides. */
  style: object,
  /** True if the container should be synced with others using react-scroll-sync. */
  synced: bool,
  /** An explicit width for the container contents (must be set if `initialOffset` is set). */
  width: number
} : {};
export default ScrollableContainer;